import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";

import { previewClientDashboard } from "@/lib/dashboard.functions";

export const Route = createFileRoute("/admin/preview/$profileId")({
  ssr: false,
  component: AdminPreviewPage,
  head: () => ({
    meta: [
      { title: "Preview client dashboard · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const STATUS_COPY: Record<string, { label: string; sub: string; tone: string }> = {
  reviewing: {
    label: "We're reviewing your request",
    sub: "Sit tight — the team is looking at your brief and will be in touch shortly.",
    tone: "bg-amber-500/10 text-amber-600 ring-amber-500/20",
  },
  onboarding: {
    label: "Onboarding in progress",
    sub: "We're spinning up your project, setting up access, and aligning on scope.",
    tone: "bg-sky-500/10 text-sky-600 ring-sky-500/20",
  },
  active: {
    label: "Active engagement",
    sub: "Your engagement is live. Expect regular updates from the team.",
    tone: "bg-emerald-500/10 text-emerald-600 ring-emerald-500/20",
  },
  paused: {
    label: "Paused",
    sub: "Your engagement is on hold. Ping us when you're ready to resume.",
    tone: "bg-foreground/10 text-foreground/70 ring-foreground/20",
  },
  completed: {
    label: "Completed",
    sub: "Wrapped. Thank you — we'd love to work together again.",
    tone: "bg-emerald-500/10 text-emerald-600 ring-emerald-500/20",
  },
};

function money(cents: number | null | undefined, currency: string | null | undefined) {
  if (cents == null) return "—";
  const sym = (currency ?? "usd").toLowerCase() === "eur" ? "€" : "$";
  return `${sym}${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
}
function date(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function AdminPreviewPage() {
  const { profileId } = Route.useParams();
  const preview = useServerFn(previewClientDashboard);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-preview", profileId],
    queryFn: () => preview({ data: { profileId } }),
  });

  return (
    <div className="min-h-dvh bg-background">
      {/* Admin preview banner */}
      <div className="sticky top-0 z-40 border-b border-foreground/10 bg-foreground text-background">
        <div className="mx-auto max-w-4xl px-6 py-3 flex items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-3">
            <span className="font-mono uppercase tracking-[0.2em] text-[10px] text-background/60">
              Admin preview
            </span>
            <span className="text-background/80">
              Read-only view of what this client sees on their dashboard.
            </span>
          </div>
          <Link
            to="/admin"
            className="underline underline-offset-4 text-background/80 hover:text-background"
          >
            ← Back to admin
          </Link>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-32" role="status" aria-live="polite">
          <p className="text-foreground/50 font-mono text-xs tracking-widest uppercase">Loading preview…</p>
        </div>
      )}
      {error && (
        <div className="flex items-center justify-center py-32 px-6" role="alert">
          <p className="text-destructive">
            Could not load preview: {(error as Error).message}
          </p>
        </div>
      )}
      {data && <PreviewBody data={data} />}
    </div>
  );
}

function PreviewBody({
  data,
}: {
  data: Awaited<ReturnType<typeof previewClientDashboard>>;
}) {
  const profile = data.profile;
  const status = data.status;
  const statusCopy = STATUS_COPY[status.value] ?? STATUS_COPY.reviewing;
  const subscriptions = data.stripe.subscriptions;
  const charges = data.stripe.charges;
  const pendingQuote = data.pending_quote;

  // Wrap the entire interactive surface in a pointer-events-none layer so the
  // admin can't accidentally trigger Stripe portal, cancellations, or payment
  // links from inside this preview.
  return (
    <div className="pointer-events-none select-text">
      <main id="main" tabIndex={-1} className="mx-auto max-w-4xl px-6 py-12 md:py-20">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
          the Vibing Company®
        </p>

        <h1 className="mt-12 font-display text-5xl md:text-6xl tracking-tight text-foreground leading-[1.02]">
          {profile?.name ? `Hey ${profile.name.split(" ")[0]}.` : "Welcome back."}
        </h1>
        <p className="mt-3 text-foreground/55">{profile?.email}</p>

        <div className="mt-12 rounded-3xl border border-foreground/10 bg-white p-8 md:p-10">
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ring-1 ${statusCopy.tone}`}
            >
              <span className="size-1.5 rounded-full bg-current" />
              {status.value}
            </span>
            {status.updated_at && (
              <span className="font-mono text-[10px] tracking-widest uppercase text-foreground/40">
                Updated {date(status.updated_at)}
              </span>
            )}
          </div>
          <h2 className="mt-5 font-display text-3xl md:text-4xl tracking-tight text-foreground leading-tight">
            {statusCopy.label}
          </h2>
          <p className="mt-3 text-foreground/60 max-w-prose">{status.note ?? statusCopy.sub}</p>
        </div>

        {pendingQuote && subscriptions.length === 0 && (
          <div className="mt-8 rounded-3xl border border-foreground/15 bg-foreground text-background p-8 md:p-10">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-background/60">
              Your proposal
            </p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl tracking-tight leading-tight">
              {money(pendingQuote.amount_cents, pendingQuote.currency)} — ready when you are
            </h2>
            <p className="mt-3 text-background/70 max-w-prose">
              Complete payment to kick off your engagement. You'll be redirected to a secure Stripe checkout.
            </p>
            <div className="mt-6">
              <span className="inline-flex items-center rounded-full bg-background text-foreground px-6 py-3 text-sm font-medium tracking-wide opacity-90">
                Pay now →
              </span>
            </div>
          </div>
        )}

        <section className="mt-10">
          <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            Subscription
          </h3>
          {subscriptions.length === 0 ? (
            <p className="mt-4 text-sm text-foreground/55">No active subscription.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {subscriptions.map((s) => (
                <li key={s.id} className="rounded-2xl border border-foreground/10 bg-white p-5">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-xl tracking-tight text-foreground capitalize">
                        {s.plan_name?.replace(/_/g, " ") ?? "Plan"}
                      </p>
                      <p className="text-xs text-foreground/55 mt-1">
                        {money(s.amount, s.currency)}
                        {s.interval ? ` / ${s.interval}` : ""} ·{" "}
                        {s.cancel_at_period_end
                          ? `Ends ${date(s.current_period_end)}`
                          : `Renews ${date(s.current_period_end)}`}
                      </p>
                    </div>
                    <span
                      className={`text-[11px] uppercase tracking-wider font-mono ${
                        s.status === "active" || s.status === "trialing"
                          ? "text-emerald-600"
                          : s.status === "past_due"
                            ? "text-amber-600"
                            : "text-foreground/50"
                      }`}
                    >
                      {s.cancel_at_period_end ? "ending" : s.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-10">
          <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            Purchases
          </h3>
          {charges.length === 0 ? (
            <p className="mt-4 text-sm text-foreground/55">No purchases yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-foreground/10 rounded-2xl border border-foreground/10 bg-white overflow-hidden">
              {charges.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-4 px-5 py-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{c.description ?? "Purchase"}</p>
                    <p className="text-xs text-foreground/55">{date(c.created)}</p>
                  </div>
                  <span className="font-mono text-sm text-foreground">
                    {money(c.amount, c.currency)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-10 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-foreground/10 bg-white p-6">
            <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              Contact on file
            </h3>
            <p className="mt-3 text-foreground">{profile?.name ?? "—"}</p>
            <p className="text-sm text-foreground/60">{profile?.email}</p>
          </div>
          <div className="rounded-2xl border border-foreground/10 bg-white p-6">
            <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              Talk to us
            </h3>
            <p className="mt-3 text-sm text-foreground/60">
              Questions, scope changes, anything else — we're one message away.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
